import {
  type BigDecimal,
  absoluteValue,
  createBigDecimal,
  minus,
  plus,
  times,
} from '@0xtorch/big-decimal'
import type { Action, Transfer } from '@0xtorch/core'
import {
  ApiSecretRequiredException,
  AssetNotFoundException,
} from '../../errors'
import type { FetchApiData } from '../../types'
import { createGetAsset } from '../../utils/createGetAsset'
import { backpackAccountCategory } from '../constants'
import { createClient } from '../createClient'
import { type Fill, fillsResponseSchema } from './schema'

/**
 * Backpack の naive ISO date-time をミリ秒 UNIX time に変換する。
 * タイムゾーンが省略されていれば UTC として解釈する。
 */
const parseTimestamp = (value: string): number => {
  const hasTimezone = /([Zz]|[+-]\d{2}:?\d{2})$/.test(value)
  const isoString = hasTimezone ? value : `${value}Z`
  const ms = Date.parse(isoString)
  if (Number.isNaN(ms)) {
    throw new Error(`Invalid Backpack timestamp value: ${value}`)
  }
  return ms
}

/**
 * Backpack の `symbol` をペアに分解する。
 * `SOL_USDC_PERP` -> { base: 'SOL', quote: 'USDC', isPerp: true }
 * `SOL_USDC`      -> { base: 'SOL', quote: 'USDC', isPerp: false }
 */
const parseSymbol = (
  symbol: string,
): { base: string; quote: string; isPerp: boolean } => {
  const parts = symbol.split('_')
  if (parts.length < 2) {
    throw new Error(`Invalid Backpack market symbol: ${symbol}`)
  }
  const isPerp = parts[parts.length - 1] === 'PERP'
  if (isPerp) {
    if (parts.length < 3) {
      throw new Error(`Invalid Backpack perp symbol: ${symbol}`)
    }
    return { base: parts[0], quote: parts[1], isPerp: true }
  }
  return { base: parts[0], quote: parts[1], isPerp: false }
}

/**
 * Backpack 約定履歴を取得する。
 * 現物・perp で挙動が異なる:
 * - 現物: side / qty / price から trade transfers を構成し、`fee` を反映した trade action を生成
 * - perp: 実現損益は `/wapi/v1/history/fills` から直接取得できないため、`fee` のみを記録
 */
export const fetchApiData: FetchApiData = async ({
  apiKey,
  apiSecret,
  assets,
  config,
  createHeaders,
  from,
  proxy,
  symbolAssetMap,
  to,
}) => {
  if (!apiSecret) {
    throw new ApiSecretRequiredException()
  }

  const getAsset = createGetAsset({ assets, symbolAssetMap })

  const client = createClient({
    apiKey,
    apiSecret,
    createHeaders,
    proxy,
  })

  const query: Record<string, string> = {
    limit: config.limit.toString(),
    sortDirection: 'Asc',
  }
  if (from) {
    query.from = from.toString()
  }
  if (to) {
    query.to = to.toString()
  }

  const response = await client.run({
    method: 'GET',
    path: '/wapi/v1/history/fills',
    query,
    responseSchema: fillsResponseSchema,
  })

  const fills = response.map((fill) => ({
    ...fill,
    timestampMs: parseTimestamp(fill.timestamp),
  }))
  fills.sort((a, b) => a.timestampMs - b.timestampMs)

  let fetchedFrom: number | undefined
  let fetchedTo: number | undefined
  const sources: string[] = []
  const actions: Action[] = []

  // (orderId, timestampMs) でグループ化してまとめて処理する
  const groups = new Map<string, typeof fills>()
  for (const fill of fills) {
    fetchedFrom =
      fetchedFrom === undefined
        ? fill.timestampMs
        : Math.min(fetchedFrom, fill.timestampMs)
    fetchedTo =
      fetchedTo === undefined
        ? fill.timestampMs
        : Math.max(fetchedTo, fill.timestampMs)

    const key = `${fill.orderId}_${fill.timestampMs}`
    const group = groups.get(key) ?? []
    group.push(fill)
    groups.set(key, group)
  }

  for (const group of groups.values()) {
    const first = group[0]
    const { base, quote, isPerp } = parseSymbol(first.symbol)

    if (isPerp) {
      processPerpGroup({
        actions,
        config,
        feeGetter: getAsset,
        group,
      })
    } else {
      processSpotGroup({
        actions,
        base,
        config,
        first,
        getAsset,
        group,
        quote,
      })
    }
  }

  const sortedActions = actions.sort((a, b) => a.timestamp - b.timestamp)

  return {
    actions: sortedActions,
    count: fills.length,
    from: fetchedFrom,
    sources,
    stopped: false,
    to: fetchedTo,
  }
}

/**
 * 現物約定グループを 1 つの trade action にまとめる。
 * 同一 orderId かつ同一 timestamp の fills は単一の trade として扱い、fee を含めて transfers を構成する。
 */
const processSpotGroup = (params: {
  actions: Action[]
  base: string
  config: { type: string }
  first: Fill & { timestampMs: number }
  getAsset: ReturnType<typeof createGetAsset>
  group: (Fill & { timestampMs: number })[]
  quote: string
}): void => {
  const { actions, base, config, first, getAsset, group, quote } = params

  const baseAsset = getAsset(base)
  const quoteAsset = getAsset(quote)
  if (!baseAsset) {
    throw new AssetNotFoundException(base)
  }
  if (!quoteAsset) {
    throw new AssetNotFoundException(quote)
  }

  const feeAsset = getAsset(first.feeSymbol)
  if (!feeAsset) {
    throw new AssetNotFoundException(first.feeSymbol)
  }
  const assetsById = [baseAsset, quoteAsset, feeAsset]

  // 集計
  const changes = new Map<string, BigDecimal>()
  for (const fill of group) {
    const total = times(fill.quantity, fill.price)
    if (fill.side === 'Bid') {
      // 買い: base を受け取り quote を払う
      const prevBase = changes.get(baseAsset.id) ?? createBigDecimal(0n)
      changes.set(baseAsset.id, plus(prevBase, fill.quantity))
      const prevQuote = changes.get(quoteAsset.id) ?? createBigDecimal(0n)
      changes.set(quoteAsset.id, minus(prevQuote, total))
    } else {
      // 売り: base を払い quote を受け取る
      const prevBase = changes.get(baseAsset.id) ?? createBigDecimal(0n)
      changes.set(baseAsset.id, minus(prevBase, fill.quantity))
      const prevQuote = changes.get(quoteAsset.id) ?? createBigDecimal(0n)
      changes.set(quoteAsset.id, plus(prevQuote, total))
    }
    if (fill.fee.value !== 0n) {
      const prevFee = changes.get(feeAsset.id) ?? createBigDecimal(0n)
      changes.set(feeAsset.id, minus(prevFee, fill.fee))
    }
  }

  const transfers: Transfer[] = []
  for (const [id, amount] of changes.entries()) {
    if (amount.value === 0n) {
      continue
    }
    const asset = assetsById.find((a) => a.id === id)
    if (!asset) {
      throw new AssetNotFoundException(id)
    }
    transfers.push({
      asset,
      amount: absoluteValue(amount),
      direction: amount.value > 0n ? 'in' : 'out',
    })
  }

  if (transfers.length === 0) {
    return
  }

  actions.push({
    comment: `Trade ${first.symbol} (orderId: ${first.orderId})`,
    evidence: 'system-rule',
    order: 0,
    source: `${backpackAccountCategory}_${config.type}_${first.orderId}_${first.timestampMs}`,
    type: 'trade',
    timestamp: first.timestampMs,
    transfers,
  })
}

/**
 * Perp 約定グループの手数料を記録する。
 * Backpack の `/wapi/v1/history/fills` は perp 約定の実現損益を含まないため、
 * 損益は別途 `pnlHistoryQueryAll` 等の利用が必要。ここでは fee のみ Action 化する。
 */
const processPerpGroup = (params: {
  actions: Action[]
  config: { type: string }
  feeGetter: ReturnType<typeof createGetAsset>
  group: (Fill & { timestampMs: number })[]
}): void => {
  const { actions, config, feeGetter, group } = params
  const first = group[0]

  let totalFee = createBigDecimal(0n)
  for (const fill of group) {
    totalFee = plus(totalFee, fill.fee)
  }
  if (totalFee.value === 0n) {
    return
  }

  const feeAsset = feeGetter(first.feeSymbol)
  if (!feeAsset) {
    throw new AssetNotFoundException(first.feeSymbol)
  }

  actions.push({
    comment: `Trade Fee of ${first.symbol}`,
    evidence: 'system-rule',
    order: 0,
    source: `${backpackAccountCategory}_${config.type}_fee_${first.orderId}_${first.timestampMs}`,
    type: totalFee.value > 0n ? 'fee' : 'income',
    timestamp: first.timestampMs,
    transfers: [
      {
        asset: feeAsset,
        amount: absoluteValue(totalFee),
        direction: totalFee.value > 0n ? 'out' : 'in',
      },
    ],
  })
}
